<script setup>
const metricCards = [
    {
        value: "300K+",
        label: "Steam Wishlists Each Show",
        icon: "https://wishlisted.s3.amazonaws.com/images/frames/icon-frame-1.svg",
        rotation: "-1.31deg",
    },
    {
        value: "280K",
        label: "Peak Viewers",
        icon: "https://wishlisted.s3.amazonaws.com/images/frames/icon-frame-2.svg",
        rotation: "4.72deg",
    },
    {
        value: "100K+",
        label: "Average Viewers",
        icon: "https://wishlisted.s3.amazonaws.com/images/frames/icon-frame-3.svg",
        rotation: "4.72deg",
    },
    {
        value: "200+",
        label: "Co-Streamers",
        icon: "https://wishlisted.s3.amazonaws.com/images/frames/icon-frame-4.svg",
        rotation: "-1.31deg",
    },
];

const cardPositions = [
    "col-start-1 row-start-1 md:col-start-1 md:row-start-1",
    "col-start-2 row-start-1 md:col-start-2 md:row-start-1",
    "col-start-1 row-start-2 md:col-start-3 md:row-start-2",
    "col-start-2 row-start-3 md:col-start-4 md:row-start-2",
];
</script>

<template>
    <section
        id="why"
        class="section-why relative flex flex-col w-full bg-black"
    >
        <div class="container">
            <div
                class="flex flex-col gap-[64px] md:gap-[176px] items-start w-full"
            >
                <!-- ===== HEADLINE AREA ===== -->
                <div
                    class="relative flex flex-col gap-[56px] w-full md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] md:gap-x-[80px] md:gap-y-[80px]"
                >
                    <!-- Ribbon SVG behind headline -->
                    <img
                        src="https://wishlisted.s3.amazonaws.com/images/ribbons/ribbon-why.svg"
                        alt=""
                        aria-hidden="true"
                        class="md:scale-150 md:object-cover absolute left-[-133px] md:top-[90px] h-[820px] w-full"
                    />

                    <!-- Section label -->
                    <div class="relative flex items-start gap-[8px]">
                        <img
                            src="https://wishlisted.s3.amazonaws.com/images/decorative/diamond-yellow.svg"
                            alt=""
                            aria-hidden="true"
                            class="mt-[2px] h-[12px] w-[12px] shrink-0"
                        />
                        <span
                            class="font-obviously font-bold text-[10px] md:text-[12px] uppercase leading-[1.4] tracking-[-0.1px] md:tracking-[-0.12px] text-primary"
                        >
                            Why should you apply?
                        </span>
                    </div>

                    <!-- Headline + description -->
                    <div
                        class="relative flex flex-col gap-[40px] items-start md:max-w-[872px]"
                    >
                        <h2
                            class="font-obviously font-bold text-[32px] md:text-[56px] leading-none text-white"
                        >
                            Our goal is simple: Get games
                            <span class="text-primary">Wishlisted.&nbsp;</span>
                        </h2>
                        <p
                            class="font-obviously-narrow text-[14px] md:text-[16px] leading-[1.4] tracking-[0.14px] md:tracking-[0.16px] text-white"
                        >
                            We call it Wishlisted because that's what happens to
                            games in the showcase.
                        </p>
                    </div>
                </div>

                <!-- ===== CARDS GRID ===== -->
                <div
                    class="relative grid h-[654px] w-full grid-cols-[repeat(2,minmax(0,1fr))] grid-rows-[repeat(3,minmax(0,1fr))] gap-[16px] md:h-[495px] md:grid-cols-[repeat(4,minmax(0,1fr))] md:grid-rows-[repeat(2,minmax(0,1fr))] md:gap-[24px]"
                >
                    <!-- Metric cards -->
                    <div
                        v-for="(card, index) in metricCards"
                        :key="index"
                        :class="cardPositions[index]"
                        class="relative flex flex-col lg:min-w-[250px] justify-between px-[20px] py-[24px] md:px-[40px] overflow-visible"
                    >
                        <!-- Rotated border -->
                        <div
                            class="pointer-events-none absolute inset-0 border-4 border-accent bg-black"
                            :style="{ transform: `rotate(${card.rotation})` }"
                        />

                        <div
                            class="relative z-10 flex h-full flex-col justify-between"
                        >
                            <img
                                :src="card.icon"
                                :alt="card.label"
                                class="h-[24px] w-[24px] self-start"
                            />

                            <span
                                class="font-obviously font-bold text-[32px] lg:text-[56px] leading-none text-primary"
                            >
                                {{ card.value }}
                            </span>

                            <span
                                class="self-end font-obviously font-bold text-[14px] text-right tracking-[-0.14px] text-primary"
                            >
                                {{ card.label }}
                            </span>
                        </div>
                    </div>

                    <!-- Text cell -->
                    <div
                        class="col-start-2 row-start-2 md:col-start-2 md:row-start-2 relative flex h-[208px] md:h-[292px] gap-[8px] items-center md:items-end self-end rounded-[8px] p-[12px] md:p-[16px]"
                    >
                        <p
                            class="font-obviously-narrow text-[14px] md:text-[16px] leading-[1.4] tracking-[0.14px] md:tracking-[0.16px] text-white"
                        >
                            Think your game is the next big hit? Apply now for
                            your chance to have it seen by an online audience of
                            millions.
                        </p>
                    </div>

                    <!-- Stars decoration: mobile only -->
                    <div
                        class="col-start-1 row-start-3 relative p-[16px] md:hidden"
                        aria-hidden="true"
                    >
                        <div class="relative inline-grid">
                            <img
                                src="https://wishlisted.s3.amazonaws.com/images/decorative/star-large.svg"
                                alt=""
                                aria-hidden="true"
                                class="pointer-events-none col-start-1 row-start-1 self-start justify-self-end w-[46px] h-[45px] rotate-[15.66deg]"
                            />
                            <img
                                src="https://wishlisted.s3.amazonaws.com/images/decorative/star-small.svg"
                                alt=""
                                aria-hidden="true"
                                class="pointer-events-none col-start-1 row-start-1 self-end justify-self-start w-[26px] h-[25px] mt-[70%] -rotate-[8.12deg]"
                            />
                        </div>
                    </div>

                    <!-- Desktop decorative stars -->
                    <div class="hidden md:contents" aria-hidden="true">
                        <img
                            src="https://wishlisted.s3.amazonaws.com/images/decorative/star-decorative-2.svg"
                            alt=""
                            aria-hidden="true"
                            class="pointer-events-none absolute bottom-[16.53%] left-[15.03%] w-[36px] h-[34px] -rotate-[11.32deg]"
                        />
                        <img
                            src="https://wishlisted.s3.amazonaws.com/images/decorative/star-decorative-3.svg"
                            alt=""
                            aria-hidden="true"
                            class="pointer-events-none absolute top-[75.35%] right-[17%] w-[20px] h-[20px] -rotate-[11.32deg]"
                        />
                        <img
                            src="https://wishlisted.s3.amazonaws.com/images/decorative/star-decorative-4.svg"
                            alt=""
                            aria-hidden="true"
                            class="pointer-events-none absolute bottom-[16.8%] left-[15.81%] w-[11px] h-[14px] -rotate-[11.32deg]"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
